import { useState, useEffect } from 'react'
import Modal from 'aldoo-ra/Modal'
import ContentEditor from 'aldoo-ra/CMS/content-editor'
import { showNotification } from 'aldoo-ra/Notification'
import { SaveCMSContent, GetCMSContent } from 'aldoo-ra/CMS/api'
import TypeResolver from 'aldoo-ra/CMS/type-resolver'
import { getPreSaveFormatter } from 'aldoo-ra/CMS/content-pre-save-formatter'

const AldooPageDialog = ({ isOpen, onClose, page }) => {
  const { getContentType } = TypeResolver()
  const AldooPageType = getContentType('AldooPage')

  const [selectedInstance, setSelectedInstance] = useState(null)

  useEffect(() => {
    if (page) {
      // Editing existing page - flatten content fields for ContentEditor
      const content = { ...page.content }

      // Clean up corrupted nested _id if it exists in content
      if (content._id && typeof content._id === 'object') {
        delete content._id
      }

      setSelectedInstance({
        _id: page._id,
        ...content
      })
    } else {
      // Creating new page
      setSelectedInstance(null)
    }
  }, [page, isOpen])

  const handleSave = async (fieldValues) => {
    // Check if a page with this id already exists (only for new pages)
    if (!page?._id && fieldValues.id) {
      const existingResponse = await GetCMSContent({
        match: {
          'content.type': 'AldooPage',
          'content.id': fieldValues.id.trim()
        },
        itemsPerPage: 1
      })

      if (existingResponse.result && existingResponse.data && existingResponse.data.length > 0) {
        showNotification({
          message: `A page with ID "${fieldValues.id}" already exists`,
          className: 'bg-admin_error text-white'
        })
        return null
      }
    }

    // ContentEditor passes the field values, we need to wrap them in content object
    let contentData = {
      ...fieldValues,
      type: 'AldooPage'
    }

    // Apply pre-save formatter to normalize data
    const preFormatter = getPreSaveFormatter('AldooPage')
    if (preFormatter) {
      contentData = preFormatter(contentData)
    }

    const savePayload = {
      content: contentData
    }

    // If editing, include the _id at root level
    if (page?._id) {
      savePayload._id = page._id
    }

    const response = await SaveCMSContent({ payload: savePayload })

    if (response.result) {
      showNotification({
        message: `Page "${fieldValues.name || fieldValues.id}" ${page ? 'updated' : 'created'} successfully`,
        className: 'bg-admin_primary text-admin_text_inv'
      })
      onClose()
      return response
    } else {
      showNotification({
        message: response.error || 'Failed to save page',
        className: 'bg-admin_error text-white'
      })
      return null
    }
  }

  const handleClose = () => {
    setSelectedInstance(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={page ? 'Edit Page' : 'Create Page'}
      variant="admin"
      className="w-full md:w-[90%] xl:w-[1200px]"
      zIndex={200}
    >
      <ContentEditor
        contentType={AldooPageType}
        fieldValues={selectedInstance || {}}
        onSave={handleSave}
        onClickSave={(result) => {
          // Only close if save was successful
          if (!result) return
          handleClose()
        }}
        onCancel={handleClose}
      />
    </Modal>
  )
}

export default AldooPageDialog